/**
 * SEOListingPage — public SEO landing page at `/best-<event>-<vendor>-<city>`.
 *
 * Resolves the URL slug via `/api/seo/parse-slug`, then renders the matching
 * vendor listing with a crawler-friendly <h1> + <title> + meta-description.
 * The page also exposes a "Get instant pricing →" CTA that deep-links into
 * the existing Instant Pricing flow with the parsed filters pre-filled.
 *
 * URL examples:
 *   /best-wedding-photographer-chennai
 *   /top-mehendi-artist-hyderabad
 *   /best-caterer-coimbatore           (no specific event)
 */
import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Link, useNavigate, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { MapPin, Star, ArrowRight, Sparkle } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import BrandLogo from '../components/BrandLogo';
import Footer from '../components/Footer';
import ShareButtons from '../components/ShareButtons';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SEOListingPage = () => {
  const { seoSlug } = useParams();
  const fullSlug = seoSlug || '';
  const navigate = useNavigate();
  const [parsing, setParsing] = useState(true);
  const [parsed, setParsed] = useState(null);
  const [listing, setListing] = useState({ items: [], count: 0 });
  const [loadingItems, setLoadingItems] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setParsing(true);
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/seo/parse-slug`,
          { params: { slug: fullSlug } });
        if (cancelled) return;
        setParsed(data);
        if (data?.valid) {
          setLoadingItems(true);
          const params = {
            category: data.category_slug,
            ...(data.event_value ? { event: data.event_value } : {}),
            ...(data.city_slug ? { city: data.city_slug } : {}),
            limit: 50,
          };
          const lr = await axios.get(`${BACKEND_URL}/api/seo/listing`, { params });
          if (!cancelled) setListing(lr.data || { items: [], count: 0 });
          setLoadingItems(false);
        }
      } catch {
        if (!cancelled) setParsed({ valid: false, reason: 'fetch failed' });
      }
      if (!cancelled) setParsing(false);
    })();
    return () => { cancelled = true; };
  }, [fullSlug]);

  // ----- Title / meta / h1 from parsed slug ----
  const { h1, title, description } = useMemo(() => {
    if (!parsed?.valid) return { h1: '', title: '', description: '' };
    const prefixWord = parsed.prefix === 'top' ? 'Top' : 'Best';
    const evt = parsed.event_label ? ` ${parsed.event_label}` : '';
    const cat = parsed.category_label || '';
    const city = parsed.city_label ? ` in ${parsed.city_label}` : '';
    const h1Text = `${prefixWord}${evt} ${cat}${city}`.replace(/\s+/g, ' ').trim();
    const titleText = `${h1Text} | BeeWeddy`;
    const desc = `Browse verified ${cat.toLowerCase()}${city.toLowerCase() ? city.toLowerCase() : ''}` +
      `${evt ? ' for ' + parsed.event_label.toLowerCase() + ' events' : ''}. ` +
      `Get instant pricing, compare packages and book on BeeWeddy.`;
    return { h1: h1Text, title: titleText, description: desc };
  }, [parsed]);

  if (parsing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-sand-50">
        <div className="text-sm text-text-secondary">Loading…</div>
      </div>
    );
  }

  // Unknown slug → bounce home so we never expose a 404 to crawlers.
  if (!parsed?.valid) return <Navigate to="/" replace />;

  const goToInstantPricing = (vendorId = null) => {
    // Pre-fill via query params; InstantPricingPage already reads `?event=` etc.
    const sp = new URLSearchParams();
    if (parsed.event_value) sp.set('event', parsed.event_value);
    if (parsed.city_slug) sp.set('city', parsed.city_label);
    if (parsed.category_value) sp.set('category', parsed.category_value);
    if (vendorId) sp.set('vendor', vendorId);
    navigate(`/instant-pricing?${sp.toString()}`);
  };

  const fullUrl = (p) => (p && p.startsWith('http') ? p : `${BACKEND_URL}${p || ''}`);

  return (
    <div className="min-h-screen bg-sand-50">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={`${BACKEND_URL}/${fullSlug}`} />
        {/* Light JSON-LD ItemList so Google sees this as a structured listing.
            Each item also gets its own LocalBusiness entry so vendors can
            rank in Google's local-business carousel. */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            "name": h1,
            "numberOfItems": (listing.items || []).length,
            "itemListElement": (listing.items || []).slice(0, 20).map((it, i) => ({
              "@type": "ListItem",
              "position": i + 1,
              "item": {
                "@type": "LocalBusiness",
                "@id": `${BACKEND_URL}/vendors/${it.id}`,
                "name": it.name,
                "url": `${BACKEND_URL}/vendors/${it.id}`,
                "address": it.city ? {
                  "@type": "PostalAddress",
                  "addressLocality": it.city,
                  "addressCountry": "IN",
                } : undefined,
                "image": (it.thumbnails || []).slice(0, 3).map(
                  (t) => (t && t.startsWith('http') ? t : `${BACKEND_URL}${t || ''}`)
                ),
                "aggregateRating": it.rating > 0 ? {
                  "@type": "AggregateRating",
                  "ratingValue": Number(it.rating).toFixed(1),
                  "reviewCount": Number(it.rating_count || 0),
                  "bestRating": "5",
                } : undefined,
                "areaServed": it.city || undefined,
                "category": parsed.category_label,
              },
            })),
          })}
        </script>
      </Helmet>

      {/* Header */}
      <header className="border-b border-[#E8E1D5] bg-white">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" data-testid="seo-back-home"><BrandLogo /></Link>
          <Button onClick={() => goToInstantPricing()}
            className="rounded-none bg-brand-primary hover:bg-[#A84A38] text-sand-50"
            data-testid="seo-cta-instant-pricing">
            Get Instant Pricing <ArrowRight size={16} className="ml-2" weight="bold" />
          </Button>
        </div>
      </header>

      {/* Hero / H1 */}
      <section className="max-w-7xl mx-auto px-6 pt-12 pb-6">
        <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-brand-primary mb-3 flex items-center gap-2">
          <Sparkle size={14} weight="fill" /> Verified on BeeWeddy
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-text-primary leading-tight"
          data-testid="seo-h1">
          {h1}
        </h1>
        <p className="text-base text-text-secondary mt-4 max-w-3xl">
          {description}
        </p>
        <div className="mt-6">
          <ShareButtons text={h1} hashtags="BeeWeddy" />
        </div>
      </section>

      {/* Listing */}
      <section className="max-w-7xl mx-auto px-6 pb-16">
        {loadingItems ? (
          <p className="text-sm text-text-secondary py-8 text-center">Loading vendors…</p>
        ) : (listing.items || []).length === 0 ? (
          <div className="border border-[#E8E1D5] bg-white p-8 text-center"
            data-testid="seo-empty-state">
            <p className="text-base text-text-primary mb-2">
              No verified {parsed.category_label.toLowerCase()}{parsed.city_label ? ` in ${parsed.city_label}` : ''} yet.
            </p>
            <p className="text-sm text-text-secondary mb-4">
              Use Instant Pricing to see sample providers and get matched with vendors actively serving your area.
            </p>
            <Button onClick={() => goToInstantPricing()}
              className="rounded-none bg-brand-primary hover:bg-[#A84A38] text-sand-50"
              data-testid="seo-empty-cta">
              Open Instant Pricing
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {(listing.items || []).map((it) => (
              <article key={it.id}
                className="bg-white border border-[#E8E1D5] hover:border-brand-primary transition-colors"
                data-testid={`seo-vendor-${it.id}`}>
                {/* Thumbnail */}
                <div className="aspect-[4/3] bg-sand-100 relative overflow-hidden">
                  {it.thumbnails?.[0] ? (
                    <img src={fullUrl(it.thumbnails[0])}
                      alt={`${parsed.prefix === 'top' ? 'Top' : 'Best'} ${parsed.category_label.replace(/s$/, '')} ${parsed.city_label || ''} — ${it.name}`}
                      className="w-full h-full object-cover" loading="lazy"
                      onError={(e) => { e.target.style.display = 'none'; }} />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-text-secondary text-xs">
                      No photo yet
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="font-heading text-lg text-text-primary truncate"
                    data-testid={`seo-vendor-name-${it.id}`}>{it.name || 'Unnamed'}</h3>
                  <div className="text-xs text-text-secondary mt-1 flex items-center gap-3">
                    {it.city && (
                      <span className="flex items-center gap-1"><MapPin size={12} weight="fill" /> {it.city}</span>
                    )}
                    {it.rating > 0 && (
                      <span className="flex items-center gap-1 text-amber-700">
                        <Star size={12} weight="fill" /> {Number(it.rating).toFixed(1)}
                        <span className="text-text-secondary">({it.rating_count})</span>
                      </span>
                    )}
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <Link to={`/vendors/${it.id}`}
                      className="text-xs text-brand-primary hover:underline flex-1 truncate"
                      data-testid={`seo-vendor-details-${it.id}`}>View profile →</Link>
                    <Button onClick={() => goToInstantPricing(it.id)}
                      size="sm"
                      className="rounded-none bg-brand-primary hover:bg-[#A84A38] text-sand-50 text-xs h-7 px-2"
                      data-testid={`seo-vendor-cta-${it.id}`}>
                      Pricing
                    </Button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
};

export default SEOListingPage;
